import * as React from 'react';
import {
    useEditContext,
    useGetList,
    useUpdate,
    useNotify,
    useRefresh,
} from 'react-admin';
import {
    Avatar,
    Typography,
    Box,
    Grid,
    Card,
    CardActionArea,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import DeleteIcon from '@mui/icons-material/Delete';
import get from 'lodash/get';

const ExtraMaterials = ({ resource = 'pupil' }: { resource?: string }) => {
    const parent = useEditContext();
    const notify = useNotify();
    const refresh = useRefresh();
    const [update] = useUpdate();
    const { data, isLoading, refetch } = useGetList('userlicense', {
        filter: { user_guid: parent.record?.guid, only_extras: true },
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'product.name', order: 'ASC' },
    });

    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [pendingIsbn, setPendingIsbn] = React.useState<string | null>(null);
    const [pendingName, setPendingName] = React.useState<string>('');

    if (isLoading || !data || data.length === 0) return null;

    const handleRemoveClick =
        (bookisbn: string, productName: string) => (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setPendingIsbn(bookisbn);
            setPendingName(productName);
            setConfirmOpen(true);
        };

    const handleConfirmRemove = () => {
        if (!pendingIsbn) return;
        const record = parent.record;
        if (!record) return;
        const currentExtras: string[] =
            get(record, 'public.extramaterials', []) || [];
        const updatedExtras = currentExtras.filter(
            isbn => isbn !== pendingIsbn
        );
        update(
            resource,
            {
                id: record.id,
                data: {
                    ...record,
                    public: { ...record.public, extramaterials: updatedExtras },
                },
                previousData: record,
            },
            {
                onSuccess: () => {
                    notify('Lisämateriaali poistettu', { type: 'success' });
                    refetch();
                    refresh();
                },
                onError: () => {
                    notify('Virhe poistettaessa lisämateriaalia', {
                        type: 'error',
                    });
                },
            }
        );
        setConfirmOpen(false);
        setPendingIsbn(null);
        setPendingName('');
    };

    const handleCancelRemove = () => {
        setConfirmOpen(false);
        setPendingIsbn(null);
        setPendingName('');
    };

    return (
        <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                Lisämateriaalit
            </Typography>
            <Grid container spacing={2}>
                {data.map(item => {
                    const subject = get(item, 'product.public.subject', '');
                    const isbn = get(item, 'product.isbn', '');
                    const bookisbn = get(item, 'product.bookisbn', '');
                    return (
                        <Grid key={item.id} size="auto">
                            <Card
                                variant="outlined"
                                sx={{ borderRadius: 2, maxWidth: 380 }}
                            >
                                <CardActionArea
                                    href={`#/product/${item.product?.guid}`}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 2,
                                    }}
                                >
                                    <Avatar
                                        variant="rounded"
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            flexShrink: 0,
                                            mr: 2,
                                        }}
                                        src={
                                            isbn
                                                ? `https://mediapankki.otava.fi/api/v1/assets/by-isbn/${isbn}.jpg?maxWidth=80&maxHeight=80`
                                                : undefined
                                        }
                                    >
                                        <MenuBookIcon />
                                    </Avatar>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        {subject && (
                                            <Typography
                                                variant="overline"
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: '0.6rem',
                                                    color: 'text.secondary',
                                                    lineHeight: 1.4,
                                                }}
                                            >
                                                {subject}
                                            </Typography>
                                        )}
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 500 }}
                                        >
                                            {item.product?.name}
                                        </Typography>
                                    </Box>
                                    {bookisbn && (
                                        <Tooltip title="Poista lisämateriaali">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={handleRemoveClick(
                                                    bookisbn,
                                                    item.product?.name || ''
                                                )}
                                                sx={{ ml: 1 }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </CardActionArea>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
            <Dialog open={confirmOpen} onClose={handleCancelRemove}>
                <DialogTitle>Poista lisämateriaali</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Haluatko varmasti poistaa lisämateriaalin{' '}
                        <strong>{pendingName}</strong>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelRemove}>Peruuta</Button>
                    <Button
                        onClick={handleConfirmRemove}
                        color="error"
                        variant="contained"
                    >
                        Poista
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ExtraMaterials;
